import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Image, StyleSheet, TouchableOpacity } from 'react-native';

import { Box, Text as RestyleText } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { Invitation, useInvitationDetailsLazyQuery } from '@graphql/generated';
import theme from '@themes/theme';
import {
  buildSignupWithEmailUrl,
  resolveInvitationLinkParams,
} from '@utils/invitationWebUrl';
import {
  APP_STORE_URL,
  detectAppInstallation,
  isMobileBrowser,
} from '@utils/mobileDeepLink';

const InvitedUser = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let invitationToken = urlParams.get('invitation_token') ?? '';
  let type = urlParams.get('type') ?? '';
  const id = urlParams.get('id') ?? '';

  const [projectName, setProjectName] = React.useState<string | null>(null);
  const { t } = useTranslation('models');
  const navigation = useNavigation<NavigationProp<ParamListBase>>();

  const [fetchInvitationDetails, { data: invitationDetailsData }] =
    useInvitationDetailsLazyQuery();

  useEffect(() => {
    if (!invitationDetailsData) return;
    setProjectName(
      invitationDetailsData.invitationDetails.project?.name ?? null
    );
  }, [invitationDetailsData]);

  const typeMap: Record<string, Invitation> = {
    project: Invitation.Project,
    team: Invitation.Team,
    chat: Invitation.Chat,
    task: Invitation.Task,
  };
  const invitationType = typeMap[type] ?? null;

  React.useEffect(() => {
    if (invitationToken && invitationType) {
      fetchInvitationDetails({
        variables: {
          token: invitationToken,
          type: invitationType,
        },
      });
    }
  }, [invitationToken, invitationType, fetchInvitationDetails]);

  let fromInvitationURL = true;
  const path = window.location.pathname;
  // please refer https://tasktag.atlassian.net/browse/TA-5972
  if (path.startsWith('/account_invitations/')) {
    fromInvitationURL = false;
    const code = path.substring('/account_invitations/'.length);
    invitationToken = code;
    type = 'team';
  }

  const getInvitationUrlWeb = () => {
    // Build URL from origin + path so it works even when the address bar was
    // updated to /invited-user but path was captured as /account_invitations/xxx
    const origin = window.location.origin ?? '';

    if (!fromInvitationURL) {
      return buildSignupWithEmailUrl(origin, {
        token: invitationToken,
        type: 'team',
        id,
      });
    }

    // Token may be in query; if we're on /invited-user, token might be in sessionStorage
    const params = resolveInvitationLinkParams({
      token: invitationToken,
      type,
      id,
    });

    if (params.token && params.type === 'project') {
      return `${origin}/invited-user?invitation_token=${params.token}&type=project${
        params.id ? `&id=${params.id}` : ''
      }`;
    }

    return buildSignupWithEmailUrl(origin, params);
  };

  const inviteUrlMobile = type
    ? 'com.eloveit.tasktag://login/' + invitationToken + `/${type}/` + id
    : process.env.ANDROID_DEEP_LINK + invitationToken;

  React.useEffect(() => {
    if (!isMobileBrowser()) return;
    return detectAppInstallation(inviteUrlMobile ?? '', APP_STORE_URL);
  }, [inviteUrlMobile]);

  // Both web buttons lead to sign-up, which an existing account can't use
  // (TA-11035). Reset rather than push so this screen unmounts and its
  // app-store redirect timer is cancelled.
  const loginLink = (
    <Box alignItems='center' marginTop='m'>
      <RestyleText variant='webBody' color='textSecondary'>
        {t('genericInvite.alreadyHaveAccount')}
      </RestyleText>
      <Button
        variant='textweb'
        accessibilityLabel={t('onBoarding.logIn')}
        onPress={() =>
          navigation.reset({ index: 0, routes: [{ name: 'login' }] })
        }>
        {t('onBoarding.logIn')}
      </Button>
    </Box>
  );

  if (isMobileBrowser()) {
    return (
      <Box flex={1} justifyContent='center' alignItems='center'>
        <Image
          style={styles.image}
          source={require('../../../assets/appIcon.png')}
        />
        <Box width={400}>
          {projectName && (
            <Text style={styles.projectName}>
              You&apos;ve been invited to {projectName}
            </Text>
          )}
          <Text style={styles.text}>
            Checking if TaskTag app is installed...
          </Text>
          <Text style={styles.text}>
            If the app is installed, it will open automatically. Otherwise,
            you'll be redirected to the app store to download it.
          </Text>
          <TouchableOpacity
            style={styles.button}
            onPress={() => {
              if (typeof window !== 'undefined') {
                window.open(getInvitationUrlWeb(), '_blank');
              }
            }}>
            <Text style={styles.buttonText}>Continue in Web Browser</Text>
          </TouchableOpacity>
          {loginLink}
        </Box>
      </Box>
    );
  }

  return (
    <Box flex={1} justifyContent='center' alignItems='center'>
      <Image
        style={styles.image}
        source={require('../../../assets/appIcon.png')}
      />

      <Box width={400}>
        {projectName && (
          <Text style={styles.projectName}>
            You&apos;ve been invited to {projectName}
          </Text>
        )}
        <Text style={styles.text}>
          If you already have the task tag application. Please click on this
          button
        </Text>

        <TouchableOpacity
          style={styles.button}
          onPress={() => {
            if (typeof window !== 'undefined') {
              window.open(inviteUrlMobile ?? '', '_blank');
            }
          }}>
          <Text style={styles.buttonText}>Open TaskTag App</Text>
        </TouchableOpacity>

        <Text style={styles.text}>
          If you don't have the task tag application. Please click on this
          button
        </Text>

        <TouchableOpacity
          style={styles.button}
          onPress={() => {
            if (typeof window !== 'undefined') {
              window.open(getInvitationUrlWeb(), '_blank');
            }
          }}>
          <Text style={styles.buttonText}>Open TaskTag Web</Text>
        </TouchableOpacity>
        {loginLink}
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  image: {
    width: 150,
    height: 150,
  },
  projectName: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
    marginHorizontal: 20,
  },
  text: {
    margin: 30,
    fontSize: 15,
    fontWeight: '500',
    textAlign: 'center',
    marginHorizontal: 20,
  },
  button: {
    padding: 10,
    width: 200,
    borderRadius: 5,
    alignSelf: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
    marginVertical: 10,
  },
  buttonText: {
    color: theme.colors.white,
    fontWeight: '600',
  },
});

export default InvitedUser;
